/**
 * "Accept the invitation" page — where the link in the invitation email lands.
 *
 * Someone invited to an existing company must never be asked to create one of
 * their own: this page signs them in (or creates their account, without a
 * company), lists the invitations waiting for their address and drops them
 * straight into the company they accepted — no onboarding wizard, which is for
 * a tenant that has nothing but seeded defaults.
 *
 * Invitations are matched on the *email*, so the address the link carries is
 * prefilled: signing in with another one would show nothing to accept.
 */

import { CompanyAvatar } from "@/components/molecules/company-avatar";
import { EmbeddedCard } from "@/components/molecules/embedded-card";
import { StripesBackground } from "@/components/molecules/stripes";
import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useUser } from "@/features/auth/hooks/use-user";
import {
  useAcceptInvitation,
  useMyClients,
  usePendingInvitations,
} from "@/features/clients/hooks/use-clients";
import type { ClientInvitation } from "@/features/clients/types";
import { useGReCaptcha } from "@/features/utils/captcha";
import { useTranslation } from "@/features/utils/i18n";
import { useTitle } from "@/features/utils/use-title";
import {
  CaretRightIcon,
  CheckCircleIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EmailPasswordChallenge } from "../login/challenges/email-password";

export const InvitationPage = () => {
  useGReCaptcha();
  const { t } = useTranslation();
  useTitle(t("invitation.title"));
  const { isInitializing, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const invitedEmail = searchParams.get("email") || "";

  if (isInitializing) {
    return (
      <div className="flex h-dvh w-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute w-full h-full top-0 right-0 z-0 overflow-hidden">
        <StripesBackground />
      </div>
      <EmbeddedCard logo className="z-10">
        {isAuthenticated ? (
          <PendingInvitations />
        ) : (
          <InvitationAuth invitedEmail={invitedEmail} />
        )}
      </EmbeddedCard>
    </div>
  );
};

/**
 * Signing in (or creating an account) to accept an invitation. Same challenge
 * as the login page, but a brand-new account is sent to the signup form in its
 * invited mode: no company to name, and back here once it exists.
 */
const InvitationAuth = ({ invitedEmail }: { invitedEmail: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handlePasswordSuccess = async (passwordToken: string) => {
    setIsLoading(true);
    try {
      await login([passwordToken]);
    } catch (e) {
      console.error(e);
      toast.error(t("loginFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex w-full justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <CardHeader>
        <CardTitle className="text-xl">{t("invitation.title")}</CardTitle>
        <CardDescription>{t("invitation.description")}</CardDescription>
      </CardHeader>
      <EmailPasswordChallenge
        initialEmail={invitedEmail}
        onSuccess={handlePasswordSuccess}
        onSignup={(email, emailToken) =>
          navigate("/signup?invited=1", {
            state: { email, emailPreSignedToken: emailToken, invited: true },
          })
        }
      />
      <CardFooter className="flex flex-col">
        <Button
          type="button"
          variant="link"
          className="p-0 text-xs text-muted-foreground"
          onClick={() => navigate("/signup")}
        >
          {t("invitation.createCompanyInstead")}
        </Button>
      </CardFooter>
    </>
  );
};

/**
 * The invitations waiting for the signed-in user's address — and, when there
 * are none (typically: the one they came for is already accepted), a way out:
 * the companies they already belong to, and creating one. This page sits
 * outside the back-office layout, so without those it is a dead end.
 */
const PendingInvitations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data: user } = useUser();
  const { data: invitations = [], isLoading } = usePendingInvitations();
  const { data: clients = [] } = useMyClients();

  if (isLoading) {
    return (
      <div className="flex w-full justify-center py-8">
        <Spinner />
      </div>
    );
  }

  const hasInvitations = invitations.length > 0;

  return (
    <>
      <CardHeader>
        <CardTitle className="text-xl">
          {hasInvitations ? t("invitation.title") : t("invitation.noneTitle")}
        </CardTitle>
        <CardDescription>
          {hasInvitations
            ? t("invitation.pendingDescription")
            : t("invitation.noneDescription", { email: user?.email })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {invitations.map((invitation) => (
          <InvitationRow
            key={invitation.invitation.id}
            invitation={invitation}
          />
        ))}
        {!hasInvitations && (
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.location.reload()}
            >
              {t("invitation.reload")}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              // The companies page, not the landing page: that one only
              // signposts and would send them straight back here.
              onClick={() => navigate("/companies")}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t("invitation.createCompany")}
            </Button>
          </div>
        )}
        {clients.length > 0 && (
          <div className="space-y-2 pt-2">
            <div className="text-sm font-medium text-muted-foreground">
              {t("invitation.yourCompanies")}
            </div>
            {clients.map(({ client }) => (
              <button
                key={client.id}
                type="button"
                className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted"
                onClick={() => navigate(`/${client.id}/home`)}
              >
                <CompanyAvatar
                  logo={client.logo}
                  name={client.name}
                  size="sm"
                />
                <span className="min-w-0 flex-1 truncate font-medium">
                  {client.name}
                </span>
                <CaretRightIcon className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-4">
        {hasInvitations && !clients.length && (
          <Button
            type="button"
            variant="link"
            className="p-0 h-auto text-xs text-muted-foreground"
            // The companies page, not the landing page: that one only signposts
            // and would send them straight back here, invitation pending.
            onClick={() => navigate("/companies")}
          >
            {t("invitation.createCompanyInstead")}
          </Button>
        )}
        <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
          <span>{t("start.footer.connectedAs", { email: user?.email })}</span>
          <Button
            variant="link"
            className="p-0 h-auto text-xs text-red-500"
            onClick={() => logout()}
          >
            {t("start.footer.logout")}
          </Button>
        </div>
      </CardFooter>
    </>
  );
};

const InvitationRow = ({ invitation }: { invitation: ClientInvitation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const acceptInvitation = useAcceptInvitation();

  const handleAccept = async () => {
    try {
      const { membership } = await acceptInvitation.mutateAsync(
        invitation.invitation.id,
      );
      toast.success(t("companies.messages.invitationAccepted"));
      // Straight into the company they just joined: it is already set up, the
      // onboarding wizard would have nothing to ask them.
      navigate(`/${membership?.client_id || invitation.client.id}/home`, {
        replace: true,
      });
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("companies.messages.invitationAcceptFailed"),
      );
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-lg border p-3">
      <CompanyAvatar
        logo={invitation.client.logo}
        name={invitation.client.name}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{invitation.client.name}</div>
        <div className="truncate text-xs text-muted-foreground">
          {invitation.inviterEmail
            ? t("invitation.invitedBy", { email: invitation.inviterEmail })
            : null}
        </div>
      </div>
      <Button
        size="sm"
        onClick={handleAccept}
        disabled={acceptInvitation.isPending}
      >
        <CheckCircleIcon className="h-4 w-4 mr-2" />
        {t("companies.actions.accept")}
      </Button>
    </div>
  );
};
